import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, STATUS_LABEL, STATUS_COLOR, periodeLabel, hasBillingRole, receivedLabel, methodLabel } from "@/lib/billing";
import { MapPin, Wallet, Receipt, Upload, X, FileText, Image as ImageIcon, Camera, Route } from "lucide-react";

/**
 * Haversine distance (meters) — cukup akurat untuk urutan relatif.
 * Feb 2026 — Digunakan bersama fitur sortir "Terdekat/Terjauh dari saya"
 * pada halaman Tagihan Saya. Tidak melibatkan routing API.
 */
const haversineMeters = (a, b) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const isValidCoord = (lat, lon) => {
  const la = Number(lat);
  const lo = Number(lon);
  return (
    lat !== undefined && lat !== null && lat !== "" &&
    lon !== undefined && lon !== null && lon !== "" &&
    Number.isFinite(la) && Number.isFinite(lo) &&
    la >= -90 && la <= 90 && lo >= -180 && lo <= 180
  );
};

const formatDistance = (m) => {
  if (!Number.isFinite(m)) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  const km = m / 1000;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
};

/**
 * BillingKolektor — halaman utama kolektor: "Tagihan Saya".
 * Menampilkan invoice-invoice customer yang di-assign kepadanya (backend
 * sudah scope) + tombol Catat Pembayaran. UI mobile-first.
 */
export default function BillingKolektor() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  // Sep 2026 Phase 23b — Map customer_id → {alamat, no_hp} untuk
  // ditampilkan di card. Prefetch setelah invoice list load. Reuse
  // endpoint existing `/billing/customers/{cid}` (read-only, RBAC
  // sudah scoped by backend).
  const [customerMap, setCustomerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Feb 2026 — Filter periode (bulan+tahun). Default = bulan berjalan
  // (device clock). Dikirim ke backend sbg `periode_tagihan` yg sudah
  // existing. Format `YYYY-MM`.
  const [periodeFilter, setPeriodeFilter] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selected, setSelected] = useState(null); // invoice utk dialog pembayaran
  const [paying, setPaying] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "TUNAI", notes: "" });
  const [payMode, setPayMode] = useState("FULL"); // "FULL" | "PARTIAL"
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState("");
  const [useMyLocation, setUseMyLocation] = useState(false);
  const [locating, setLocating] = useState(false);
  const [optimizeRoute, setOptimizeRoute] = useState(false);
  // Feb 2026 — UX: pagination atas + sort LUNAS ke bawah.
  // Konsisten dengan halaman Invoice/Customer Billing (default 10).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Feb 2026 — Sortir tagihan berdasarkan jarak lokasi kolektor.
  // Mode: NEAREST (default) | FARTHEST | SISA_DESC | DEFAULT.
  // GPS diminta sekali per session ketika mode nearest/farthest dipilih.
  const [sortMode, setSortMode] = useState("NEAREST");
  const [myLoc, setMyLoc] = useState(null); // {lat, lon}
  const [locStatus, setLocStatus] = useState("idle"); // idle|loading|granted|denied|unsupported
  // Feb 2026 — Payment-channel summary (payment-based, konsisten dgn Rekap
  // Admin per-kolektor). Mencakup pembayaran yang diterima kolektor di
  // periode berjalan, termasuk pelunasan invoice periode lain (CF-sumber).
  // Reconcile dgn Rekap Admin: channel_kolektor + channel_transfer +
  // channel_loket ≡ Pen.Kas kolektor di Rekap Admin.
  const [paySummary, setPaySummary] = useState(null);
  // Feb 2026 — Detail dialog state: tampilkan daftar transaksi per channel
  // untuk audit transparansi (user bisa trace payment mana yang masuk ke
  // kanal mana).
  const [detailDialog, setDetailDialog] = useState({ open: false, channel: null, payments: [] });
  const [paySummaryDetail, setPaySummaryDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadPaySummary = async (periode) => {
    if (!periode) { setPaySummary(null); return; }
    try {
      const { data } = await api.get("/billing/me/payment-channel-summary",
        { params: { periode_tagihan: periode } });
      setPaySummary(data);
    } catch (e) { setPaySummary(null); }
  };

  const openChannelDetail = async (channel) => {
    if (!periodeFilter) return;
    setLoadingDetail(true);
    setDetailDialog({ open: true, channel, payments: [] });
    try {
      const { data } = await api.get("/billing/me/payment-channel-summary",
        { params: { periode_tagihan: periodeFilter, include_detail: true } });
      setPaySummaryDetail(data);
      const key = channel === "TUNAI" ? "channel_kolektor_payments"
                     : channel === "TRANSFER" ? "channel_transfer_payments"
                     : "channel_loket_payments";
      setDetailDialog({ open: true, channel, payments: data[key] || [] });
    } catch (e) {
      toast.error("Gagal memuat detail transaksi");
      setDetailDialog({ open: false, channel: null, payments: [] });
    } finally {
      setLoadingDetail(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (periodeFilter) params.periode_tagihan = periodeFilter;
      const { data } = await api.get("/billing/invoices", { params });
      const items = data.items || [];
      setInvoices(items);
      // Prefetch customer detail (alamat + no_hp) untuk unique customers.
      // Read-only, tidak mutate. Kegagalan per-customer tidak stop UI.
      const uniqCids = Array.from(new Set(items.map((i) => i.customer_id).filter(Boolean)));
      if (uniqCids.length > 0) {
        const results = await Promise.all(uniqCids.map(
          (cid) => api.get(`/billing/customers/${cid}`).then((r) => [cid, r.data]).catch(() => [cid, null])
        ));
        const map = {};
        results.forEach(([cid, cust]) => {
          if (cust) map[cid] = {
            alamat: cust.alamat || "",
            no_hp: cust.no_hp || "",
            latitude: cust.latitude,
            longitude: cust.longitude,
          };
        });
        setCustomerMap(map);
      } else {
        setCustomerMap({});
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat tagihan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (hasBillingRole(user, "kolektor", "admin", "billing")) { load(); loadPaySummary(periodeFilter); } /* eslint-disable-next-line */ }, [statusFilter, periodeFilter, user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return invoices;
    return invoices.filter((i) =>
      (i.customer_name_snapshot || "").toLowerCase().includes(s) ||
      (i.customer_id || "").toLowerCase().includes(s) ||
      (i.invoice_id || "").toLowerCase().includes(s)
    );
  }, [invoices, q]);

  // Feb 2026 — Request GPS SEKALI per session ketika mode sortir
  // membutuhkan lokasi (NEAREST/FARTHEST) dan belum ada koordinat.
  // Tidak polling; kegagalan tidak error—fallback ke DEFAULT via
  // effectiveMode di sortedFiltered.
  useEffect(() => {
    if (sortMode !== "NEAREST" && sortMode !== "FARTHEST") return;
    if (myLoc || locStatus !== "idle") return;
    if (!("geolocation" in navigator)) {
      setLocStatus("unsupported");
      return;
    }
    setLocStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const la = pos.coords?.latitude;
        const lo = pos.coords?.longitude;
        if (Number.isFinite(la) && Number.isFinite(lo)) {
          setMyLoc({ lat: la, lon: lo });
          setLocStatus("granted");
        } else {
          setLocStatus("denied");
        }
      },
      () => setLocStatus("denied"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [sortMode, myLoc, locStatus]);

  // Feb 2026 — Sort stabil dengan mode:
  //   NEAREST/FARTHEST → dalam GROUP outstanding, urutkan berdasarkan
  //     Haversine distance dari lokasi kolektor. Customer tanpa koord
  //     valid dijatuhkan ke akhir grup (deterministic: order asli).
  //   SISA_DESC → sisa_tagihan descending dalam GROUP outstanding.
  //   DEFAULT   → urutan asli (backward compatible).
  // LUNAS SELALU di paling bawah (grup 2), status lain di tengah.
  const sortedFiltered = useMemo(() => {
    const rank = (st) => {
      if (st === "BELUM_LUNAS" || st === "SEBAGIAN") return 0;
      if (st === "LUNAS") return 2;
      return 1;
    };
    // Jika mode butuh GPS tapi lokasi belum tersedia → fallback DEFAULT.
    const effectiveMode =
      (sortMode === "NEAREST" || sortMode === "FARTHEST") && !myLoc
        ? "DEFAULT"
        : sortMode;
    const distFor = (inv) => {
      if (!myLoc) return Infinity;
      const c = customerMap[inv.customer_id];
      if (!c || !isValidCoord(c.latitude, c.longitude)) return Infinity;
      return haversineMeters(
        { lat: myLoc.lat, lon: myLoc.lon },
        { lat: Number(c.latitude), lon: Number(c.longitude) }
      );
    };
    const withIdx = filtered.map((inv, idx) => ({ inv, idx, r: rank(inv.status) }));
    withIdx.sort((a, b) => {
      if (a.r !== b.r) return a.r - b.r;
      // Sub-sort hanya untuk GROUP 0 (outstanding). GROUP 1/2 stable.
      if (a.r !== 0) return a.idx - b.idx;
      if (effectiveMode === "NEAREST") {
        const da = distFor(a.inv);
        const db = distFor(b.inv);
        if (da === db) return a.idx - b.idx;
        // Infinity (tanpa koord) otomatis jatuh di akhir grup.
        return da - db;
      }
      if (effectiveMode === "FARTHEST") {
        const da = distFor(a.inv);
        const db = distFor(b.inv);
        // Invalid coord tetap di belakang meskipun mode "terjauh".
        const aBad = !Number.isFinite(da);
        const bBad = !Number.isFinite(db);
        if (aBad && bBad) return a.idx - b.idx;
        if (aBad) return 1;
        if (bBad) return -1;
        if (da === db) return a.idx - b.idx;
        return db - da;
      }
      if (effectiveMode === "SISA_DESC") {
        const sa = parseInt(a.inv.sisa_tagihan || 0, 10);
        const sb = parseInt(b.inv.sisa_tagihan || 0, 10);
        if (sa === sb) return a.idx - b.idx;
        return sb - sa;
      }
      // DEFAULT — urutan asli.
      return a.idx - b.idx;
    });
    return withIdx.map((x) => x.inv);
  }, [filtered, sortMode, myLoc, customerMap]);

  // Peta customer_id → distance meters (untuk label per card).
  // Hanya dihitung ketika lokasi tersedia.
  const distanceByCustomer = useMemo(() => {
    if (!myLoc) return {};
    const m = {};
    for (const inv of filtered) {
      const cid = inv.customer_id;
      if (!cid || m[cid] !== undefined) continue;
      const c = customerMap[cid];
      if (!c || !isValidCoord(c.latitude, c.longitude)) continue;
      m[cid] = haversineMeters(
        { lat: myLoc.lat, lon: myLoc.lon },
        { lat: Number(c.latitude), lon: Number(c.longitude) }
      );
    }
    return m;
  }, [filtered, customerMap, myLoc]);

  // Total halaman + slice utk halaman aktif.
  const totalItems = sortedFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  // Guard: jika data berkurang (mis. filter berubah) → kembalikan page valid.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  // Reset ke halaman 1 ketika query / statusFilter / pageSize / sortMode berubah.
  useEffect(() => { setPage(1); }, [q, statusFilter, periodeFilter, pageSize, sortMode]);

  const pagedInvoices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedFiltered.slice(start, start + pageSize);
  }, [sortedFiltered, page, pageSize]);

  // Feb 2026 — Ringkasan strip: agregat atas seluruh invoice pada scope
  // saat ini (search + status filter aktif). Tidak dibatasi halaman aktif
  // supaya kolektor tahu total kewajiban hari ini. Client-side murni.
  const summary = useMemo(() => {
    let total = 0, dibayar = 0, sisa = 0, outstandingCount = 0;
    // Feb 2026 — Channel breakdown TUNAI/TRANSFER/LOKET(=KANTOR).
    // Dibangun dari `channel_amount_by_method` yang di-attach backend
    // per invoice. Reconcile: TUNAI+TRANSFER+LOKET ≡ sum(total_dibayar)
    // across invoice yang dihitung (sama basis dengan SUDAH DIBAYAR).
    let chTunai = 0, chTransfer = 0, chLoket = 0;
    let cntTunai = 0, cntTransfer = 0, cntLoket = 0;
    // Feb 2026 — Ringkasan status invoice (Lunas / Sebagian / Belum Lunas).
    // Dihitung per invoice unik dalam cakupan filter aktif, mengikuti aturan
    // Billing V1: invoice DIBATALKAN & invoice sumber carry-forward
    // (`carry_forward_to`) di-exclude agar tidak dobel hitung dengan
    // invoice penggantinya. Nominal per status = Σ total_harus_dibayar.
    let countLunas = 0, countSebagian = 0, countBelum = 0;
    let nominalLunas = 0, nominalSebagian = 0, nominalBelum = 0;
    for (const inv of filtered) {
      if (inv.status === "DIBATALKAN") continue;
      if (inv.carry_forward_to) continue;
      const t = parseInt(inv.total_harus_dibayar || 0, 10);
      total += t;
      dibayar += parseInt(inv.total_dibayar || 0, 10);
      const s = parseInt(inv.sisa_tagihan || 0, 10);
      sisa += s;
      if (s > 0 && inv.status !== "LUNAS") outstandingCount += 1;
      if (inv.status === "LUNAS") { countLunas += 1; nominalLunas += t; }
      else if (inv.status === "SEBAGIAN") { countSebagian += 1; nominalSebagian += t; }
      else if (inv.status === "BELUM_LUNAS") { countBelum += 1; nominalBelum += t; }
      const ch = inv.channel_amount_by_method || {};
      const cc = inv.channel_count_by_method || {};
      chTunai += parseInt(ch.TUNAI || 0, 10);
      chTransfer += parseInt(ch.TRANSFER || 0, 10);
      chLoket += parseInt(ch.KANTOR || 0, 10);
      cntTunai += parseInt(cc.TUNAI || 0, 10);
      cntTransfer += parseInt(cc.TRANSFER || 0, 10);
      cntLoket += parseInt(cc.KANTOR || 0, 10);
    }
    return { total, dibayar, sisa, outstandingCount, count: filtered.length,
             chTunai, chTransfer, chLoket, cntTunai, cntTransfer, cntLoket,
             countLunas, countSebagian, countBelum,
             nominalLunas, nominalSebagian, nominalBelum };
  }, [filtered]);

  // Bangun daftar stop unik (per customer) dari invoice yang sedang terlihat +
  // punya koordinat valid. Google Maps Directions URL praktisnya 10 stop.
  // Feb 2026 — Audit fix: rute HANYA boleh berasal dari invoice yang masih
  // memiliki outstanding balance. Kriteria eligibility (konsisten dengan
  // tombol "Bayar"): status ∈ {BELUM_LUNAS, SEBAGIAN} & belum carry-forward
  // & sisa_tagihan > 0. Invoice LUNAS/DIBATALKAN/carry-forward tidak boleh
  // menjadi dasar rute meskipun customer punya koordinat.
  const ROUTE_MAX_STOPS = 10;
  const isInvoiceOutstanding = (inv) =>
    inv && inv.status !== "LUNAS" && inv.status !== "DIBATALKAN" &&
    !inv.carry_forward_to && parseInt(inv.sisa_tagihan || 0, 10) > 0;
  const routeStops = useMemo(() => {
    const seen = new Set();
    const stops = [];
    for (const inv of pagedInvoices) {
      if (!isInvoiceOutstanding(inv)) continue;
      const cid = inv.customer_id;
      if (!cid || seen.has(cid)) continue;
      const cust = customerMap[cid];
      if (!cust) continue;
      const lat = Number(cust.latitude);
      const lon = Number(cust.longitude);
      const ok =
        cust.latitude !== undefined && cust.latitude !== null && cust.latitude !== "" &&
        cust.longitude !== undefined && cust.longitude !== null && cust.longitude !== "" &&
        Number.isFinite(lat) && Number.isFinite(lon) &&
        lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
      if (!ok) continue;
      seen.add(cid);
      stops.push({ customer_id: cid, name: inv.customer_name_snapshot, lat, lon });
    }
    return stops;
  }, [pagedInvoices, customerMap]);

  const openRoute = () => {
    if (routeStops.length === 0) return;

    // Haversine distance (meters) — cukup akurat untuk urutan relatif.
    const distMeters = (a, b) => {
      const toRad = (d) => (d * Math.PI) / 180;
      const R = 6371000;
      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lon - a.lon);
      const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(s));
    };

    // Nearest-neighbor greedy: mulai dari titik awal, iteratif pilih stop terdekat.
    const nearestNeighbor = (stops, startPoint) => {
      const remain = stops.slice();
      const out = [];
      let cur = startPoint;
      while (remain.length > 0) {
        let best = 0;
        let bestD = Infinity;
        for (let i = 0; i < remain.length; i++) {
          const d = distMeters(cur, remain[i]);
          if (d < bestD) { bestD = d; best = i; }
        }
        const pick = remain.splice(best, 1)[0];
        out.push(pick);
        cur = pick;
      }
      return out;
    };

    const doOpen = (originPair, originPoint) => {
      let stopsToUse = routeStops.slice();
      if (optimizeRoute && stopsToUse.length > 1) {
        if (originPoint) {
          // GPS available — sort semua stop dari GPS.
          stopsToUse = nearestNeighbor(stopsToUse, originPoint);
        } else {
          // Tidak ada origin — pakai stop pertama sebagai titik awal, urutkan sisanya.
          const first = stopsToUse[0];
          const rest = nearestNeighbor(stopsToUse.slice(1), first);
          stopsToUse = [first, ...rest];
        }
      }
      const use = stopsToUse.slice(0, ROUTE_MAX_STOPS);
      const parts = use.map((s) => `${s.lat},${s.lon}`);
      const path = (originPair ? [originPair, ...parts] : parts).join("/");
      const url = `https://www.google.com/maps/dir/${path}?travelmode=driving`;
      window.open(url, "_blank", "noopener,noreferrer");
      if (routeStops.length > ROUTE_MAX_STOPS) {
        toast.info(`Menampilkan ${ROUTE_MAX_STOPS} stop pertama dari ${routeStops.length}. Sisanya dapat dibuka setelah persinggahan tercapai.`);
      }
    };

    if (!useMyLocation) { doOpen(null, null); return; }

    if (!("geolocation" in navigator)) {
      toast.warning("Perangkat tidak mendukung geolokasi. Rute dibuka tanpa titik awal.");
      doOpen(null, null);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude, longitude } = pos.coords || {};
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          doOpen(`${latitude},${longitude}`, { lat: latitude, lon: longitude });
        } else {
          toast.warning("Lokasi tidak valid. Rute dibuka tanpa titik awal.");
          doOpen(null, null);
        }
      },
      (err) => {
        setLocating(false);
        const map = {
          1: "Izin lokasi ditolak. Rute dibuka tanpa titik awal.",
          2: "Lokasi tidak tersedia. Rute dibuka tanpa titik awal.",
          3: "Waktu pencarian lokasi habis. Rute dibuka tanpa titik awal.",
        };
        toast.warning(map[err?.code] || "Gagal mendapatkan lokasi. Rute dibuka tanpa titik awal.");
        doOpen(null, null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const openPay = async (inv) => {
    // Ambil detail terkini + informasi customer utk lokasi
    try {
      const [invR, custR] = await Promise.all([
        api.get(`/billing/invoices/${inv.invoice_id}`),
        api.get(`/billing/customers/${inv.customer_id}`).catch(() => ({ data: null })),
      ]);
      setSelected({ ...invR.data, _customer: custR.data });
      setPayForm({ amount: "", method: "TUNAI", notes: "" });
      setPayMode("FULL");
      setProofFile(null);
      setProofPreview("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat detail invoice");
    }
  };

  const submitPay = async (e) => {
    e?.preventDefault?.();
    if (paying) return;
    const sisa = parseInt(selected?.sisa_tagihan || 0, 10);
    // Nominal efektif: FULL → sisa, PARTIAL → input user.
    const effectiveAmount = payMode === "FULL" ? sisa : parseInt(payForm.amount, 10);
    if (!sisa || sisa < 1) {
      toast.error("Invoice sudah lunas");
      return;
    }
    if (payMode === "PARTIAL") {
      if (!effectiveAmount || effectiveAmount < 1) {
        toast.error("Jumlah pembayaran wajib diisi (minimal Rp1)");
        return;
      }
      if (effectiveAmount > sisa) {
        toast.error(`Melebihi sisa tagihan (${formatRupiah(sisa)})`);
        return;
      }
    }
    if (payForm.method === "TRANSFER" && !proofFile) {
      toast.error("Bukti transfer wajib diunggah untuk pembayaran TRANSFER");
      return;
    }
    setPaying(true);
    try {
      let proof_url = "";
      if (payForm.method === "TRANSFER" && proofFile) {
        const fd = new FormData();
        fd.append("file", proofFile);
        const up = await api.post("/billing/payments/proof-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        proof_url = up.data.proof_url;
      }
      const payload = {
        invoice_id: selected.invoice_id,
        method: payForm.method,
        notes: payForm.notes || "",
        proof_url: proof_url || undefined,
      };
      if (payMode === "FULL") {
        payload.pay_full = true;
      } else {
        payload.amount = effectiveAmount;
      }
      await api.post("/billing/payments", payload);
      toast.success(`Pembayaran ${formatRupiah(effectiveAmount)} berhasil dicatat.`);
      setSelected(null);
      setProofFile(null);
      setProofPreview("");
      await load();
      await loadPaySummary(periodeFilter);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal mencatat pembayaran");
    } finally {
      setPaying(false);
    }
  };

  const onProofFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setProofFile(null); setProofPreview(""); return; }
    // Client-side sanity: max 8 MB, image/pdf only.
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Ukuran maksimum 8 MB");
      e.target.value = "";
      return;
    }
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) {
      toast.error("Format tidak didukung. Gunakan foto (JPG/PNG/WebP/HEIC) atau PDF.");
      e.target.value = "";
      return;
    }
    setProofFile(f);
    if (f.type.startsWith("image/")) {
      const url = URL.createObjectURL(f);
      setProofPreview(url);
    } else {
      setProofPreview("");
    }
  };

  if (!hasBillingRole(user, "kolektor", "admin", "billing")) {
    return <div className="p-6" data-testid="billing-kolektor-denied">Anda tidak memiliki akses ke Billing.</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="billing-kolektor-page">
      <header>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Tagihan Saya</h1>
        <p className="text-sm text-[#6B7280]">Daftar invoice customer yang ditugaskan kepada Anda.</p>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="Cari nama / no HP / customer ID / invoice ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="kolektor-search"
        />
        <Input
          type="month"
          value={periodeFilter}
          onChange={(e) => setPeriodeFilter(e.target.value)}
          className="w-full sm:w-44"
          title="Filter periode tagihan"
          data-testid="kolektor-periode-filter"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-white"
          data-testid="kolektor-status-filter"
        >
          <option value="">Semua Status</option>
          <option value="BELUM_LUNAS">Belum Lunas</option>
          <option value="SEBAGIAN">Dibayar Sebagian</option>
          <option value="LUNAS">Lunas</option>
        </select>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-white"
          data-testid="kolektor-sort-mode"
          title="Urutkan tagihan"
        >
          <option value="NEAREST">Urutkan: Terdekat dari saya</option>
          <option value="FARTHEST">Urutkan: Terjauh dari saya</option>
          <option value="SISA_DESC">Urutkan: Sisa tagihan terbesar</option>
          <option value="DEFAULT">Urutkan: Default</option>
        </select>
      </div>
      {(sortMode === "NEAREST" || sortMode === "FARTHEST") && (
        <div className="text-xs -mt-2" data-testid="kolektor-sort-hint">
          {locStatus === "loading" && <span className="text-[#6B7280]">Mengambil lokasi Anda…</span>}
          {locStatus === "granted" && myLoc && (
            <span className="text-[#065F46]">
              Lokasi Anda terdeteksi. Tagihan diurutkan {sortMode === "NEAREST" ? "dari terdekat" : "dari terjauh"}.
            </span>
          )}
          {locStatus === "denied" && (
            <span className="text-[#92400E]">
              Lokasi tidak tersedia. Menggunakan urutan default.
            </span>
          )}
          {locStatus === "unsupported" && (
            <span className="text-[#92400E]">
              Perangkat tidak mendukung lokasi. Menggunakan urutan default.
            </span>
          )}
        </div>
      )}

      {/* Rute Kunjungan Harian — buka Google Maps Directions dgn stop customer yang sedang terlihat */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="kolektor-route-bar">
        <Button
          type="button"
          onClick={openRoute}
          disabled={routeStops.length === 0 || locating}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white disabled:opacity-50"
          data-testid="kolektor-route-open-btn"
        >
          <Route className="w-4 h-4 mr-1.5" />
          {locating ? "Mendapatkan lokasi…" : "Buka Rute Kunjungan"}
          {!locating && routeStops.length > 0 && (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-white/25 px-2 py-0.5 text-[11px] font-mono"
                  data-testid="kolektor-route-count">
              {Math.min(routeStops.length, ROUTE_MAX_STOPS)}
              {routeStops.length > ROUTE_MAX_STOPS ? `/${routeStops.length}` : ""}
            </span>
          )}
        </Button>
        <label
          className="inline-flex items-center gap-2 text-xs text-[#111827] cursor-pointer select-none"
          data-testid="kolektor-route-origin-label"
        >
          <input
            type="checkbox"
            checked={useMyLocation}
            onChange={(e) => setUseMyLocation(e.target.checked)}
            className="w-4 h-4 accent-[#0055FF]"
            data-testid="kolektor-route-origin-toggle"
          />
          Mulai dari lokasi saya
        </label>
        <label
          className="inline-flex items-center gap-2 text-xs text-[#111827] cursor-pointer select-none"
          data-testid="kolektor-route-optimize-label"
        >
          <input
            type="checkbox"
            checked={optimizeRoute}
            onChange={(e) => setOptimizeRoute(e.target.checked)}
            className="w-4 h-4 accent-[#0055FF]"
            data-testid="kolektor-route-optimize-toggle"
          />
          Susun rute otomatis
        </label>
        <div className="text-xs text-[#6B7280] basis-full sm:basis-auto" data-testid="kolektor-route-hint">
          {routeStops.length === 0
            ? "Belum ada customer berkoordinat pada daftar saat ini."
            : routeStops.length > ROUTE_MAX_STOPS
              ? `Mengambil ${ROUTE_MAX_STOPS} stop pertama dari ${routeStops.length} (batas Google Maps).`
              : `Rute akan berisi ${routeStops.length} stop customer (deduplikasi per pelanggan).`}
        </div>
      </div>

      {loading && <div className="text-sm text-[#6B7280]">Memuat…</div>}
      {!loading && totalItems === 0 && (
        <div className="text-sm text-[#6B7280] p-8 text-center border rounded-md" data-testid="kolektor-empty">
          Belum ada tagihan aktif. Hubungi Admin bila ada assignment baru.
        </div>
      )}

      {/* Feb 2026 — Pembayaran Diterima Periode (payment-based).
          Mengatasi selisih dengan Rekap Admin: payment TUNAI yang Anda
          terima di periode berjalan untuk invoice periode LAIN (misal
          invoice carry-forward sumber) tidak tercatat di channel strip
          invoice-based di atas, tetapi SELALU tercatat di sini. Reconcile
          1:1 dengan "Payment Kolektor/Transfer/Loket" di Rekap Admin. */}
      {!loading && paySummary && paySummary.total_penerimaan_kas > 0 && (
        <div className="rounded-lg border border-[#D6E7FA] bg-[#F0F7FF] p-3 sm:p-4"
             data-testid="kolektor-penerimaan-saya">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] sm:text-xs uppercase tracking-wide text-[#6B7280] font-semibold">
                Pembayaran Diterima Periode Ini
              </div>
              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <div className="font-mono font-black text-lg sm:text-xl text-[#065F46]"
                     data-testid="kolektor-penerimaan-total">
                  {formatRupiah(paySummary.total_penerimaan_kas)}
                </div>
                <div className="text-[11px] sm:text-xs text-[#065F46] font-semibold"
                     data-testid="kolektor-penerimaan-total-tx">
                  {((paySummary.count_kolektor || 0) + (paySummary.count_transfer || 0) + (paySummary.count_loket || 0)).toLocaleString("id-ID")} transaksi
                </div>
              </div>
              <div className="text-[10px] sm:text-[11px] text-[#6B7280] mt-1 leading-snug">
                Jumlah transaksi dari 3 kanal. Pembayaran CONFIRMED berdasarkan tanggal bayar, termasuk pelunasan invoice periode lain. Konsisten dengan Rekap Billing Admin.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t text-xs">
            <button type="button" className="text-left hover:bg-white rounded px-1 py-0.5 transition"
                 data-testid="kolektor-penerimaan-tunai"
                 onClick={() => paySummary.channel_kolektor > 0 && openChannelDetail("TUNAI")}>
              <div className="text-[10px] text-[#6B7280] uppercase">TUNAI (diterima saya)</div>
              <div className="font-mono font-semibold">{formatRupiah(paySummary.channel_kolektor)}</div>
              <div className="text-[9px] text-[#9CA3AF]">{paySummary.count_kolektor} tx · klik untuk detail</div>
            </button>
            <button type="button" className="text-left hover:bg-white rounded px-1 py-0.5 transition"
                 data-testid="kolektor-penerimaan-transfer"
                 onClick={() => paySummary.channel_transfer > 0 && openChannelDetail("TRANSFER")}>
              <div className="text-[10px] text-[#6B7280] uppercase">TRANSFER (customer saya)</div>
              <div className="font-mono font-semibold">{formatRupiah(paySummary.channel_transfer)}</div>
              <div className="text-[9px] text-[#9CA3AF]">{paySummary.count_transfer} tx · klik untuk detail</div>
            </button>
            <button type="button" className="text-left hover:bg-white rounded px-1 py-0.5 transition"
                 data-testid="kolektor-penerimaan-loket"
                 onClick={() => paySummary.channel_loket > 0 && openChannelDetail("LOKET")}>
              <div className="text-[10px] text-[#6B7280] uppercase">LOKET (customer saya)</div>
              <div className="font-mono font-semibold">{formatRupiah(paySummary.channel_loket)}</div>
              <div className="text-[9px] text-[#9CA3AF]">{paySummary.count_loket} tx · klik untuk detail</div>
            </button>
          </div>
          {/* Diagnostic: method anomali (bukan 3 kategori valid). */}
          {paySummary.anomaly_method_counts && Object.keys(paySummary.anomaly_method_counts).length > 0 && (
            <div className="mt-3 pt-3 border-t text-[11px] text-[#991B1B] bg-[#FEF2F2] -mx-3 sm:-mx-4 px-3 sm:px-4 py-2"
                 data-testid="kolektor-method-anomaly">
              <b>Peringatan data:</b> Terdeteksi {Object.values(paySummary.anomaly_method_counts).reduce((a,b)=>a+b,0)} pembayaran dengan metode tidak dikenali: {Object.entries(paySummary.anomaly_method_counts).map(([k,v])=>`${k} (${v} tx)`).join(", ")}. Pembayaran ini tidak masuk kanal manapun. Hubungi admin untuk audit.
            </div>
          )}
          {/* Selisih TUNAI invoice-periode vs TUNAI diterima saya. */}
          {paySummary.tunai_invoice_lain > 0 && (
            <div className="mt-3 pt-3 border-t text-[11px] text-[#92400E] bg-[#FFFBEB] -mx-3 sm:-mx-4 px-3 sm:px-4 py-2 rounded-b-lg"
                 data-testid="kolektor-tunai-lain">
              <b>Catatan:</b> Rp {formatRupiah(paySummary.tunai_invoice_lain).replace("Rp","")} ({paySummary.count_tunai_lain} tx) TUNAI yang Anda terima periode ini adalah pelunasan <b>invoice periode lain</b> (umumnya invoice bon/carry-forward). Nominal ini tidak muncul di card TUNAI invoice-periode di atas, tetapi sudah termasuk di Pembayaran Diterima Periode Ini dan di Rekap Admin.
            </div>
          )}
        </div>
      )}

      {/* Feb 2026 — Dialog detail per channel: audit transparansi untuk
          memastikan setiap pembayaran masuk ke kanal yang benar. */}
      <Dialog open={detailDialog.open} onOpenChange={(o) => !o && setDetailDialog({ open: false, channel: null, payments: [] })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
                         data-testid="kolektor-channel-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail {detailDialog.channel} — {periodeFilter}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-[#6B7280] mb-2">
            Daftar pembayaran CONFIRMED yang masuk ke kanal <b>{detailDialog.channel}</b> di periode ini. Kolom "Method DB" menampilkan nilai metode aktual yang tersimpan di database (hanya pembayaran dengan metode yang sesuai yang masuk kanal ini).
          </div>
          <div className="overflow-y-auto flex-1 -mx-6 px-6">
            {loadingDetail ? (
              <div className="text-center py-8 text-sm text-[#6B7280]">Memuat...</div>
            ) : detailDialog.payments.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#6B7280]">Tidak ada transaksi.</div>
            ) : (
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#F8F9FA] sticky top-0">
                  <tr className="border-b">
                    <th className="px-2 py-1.5 text-left">Tanggal</th>
                    <th className="px-2 py-1.5 text-left">Payment ID</th>
                    <th className="px-2 py-1.5 text-left">Invoice</th>
                    <th className="px-2 py-1.5 text-left">Customer</th>
                    <th className="px-2 py-1.5 text-right">Nominal</th>
                    <th className="px-2 py-1.5 text-left">Method DB</th>
                  </tr>
                </thead>
                <tbody>
                  {detailDialog.payments.map((p) => (
                    <tr key={p.payment_id} className="border-b hover:bg-[#F9FAFB]"
                        data-testid={`detail-row-${p.payment_id}`}>
                      <td className="px-2 py-1.5">{p.payment_date}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{p.payment_id}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{p.invoice_id}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{p.customer_id}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-semibold">{formatRupiah(p.amount_received)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.method_raw === detailDialog.channel || (p.method_raw === "KANTOR" && detailDialog.channel === "LOKET") ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>
                          {p.method_raw || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-[#F8F9FA] font-semibold">
                  <tr>
                    <td className="px-2 py-1.5" colSpan={4}>Total ({detailDialog.payments.length} tx)</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {formatRupiah(detailDialog.payments.reduce((s,p)=>s+(p.amount_received||0),0))}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDetailDialog({ open: false, channel: null, payments: [] })}
                    data-testid="detail-close">Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Ringkasan Status Invoice (invoice-based, bukan kas).
          Agregat atas seluruh invoice dalam cakupan filter aktif (periode +
          status + search). Billing V1 FROZEN: invoice DIBATALKAN & invoice
          sumber carry-forward di-exclude untuk menghindari dobel hitung.
          Selisih Rekonsiliasi = penerimaan kas (payment-based) − total invoice
          (invoice-based). Positif = ada pelunasan invoice periode LAIN
          (konsolidasi/carry-forward) ikut masuk kas periode ini — BUKAN
          penerimaan baru, hanya bantu rekonsiliasi dengan Rekap Billing Admin. */}
      {!loading && totalItems > 0 && (
        <div className="rounded-lg border bg-[#F9FAFB] p-3 sm:p-4"
             data-testid="kolektor-ringkasan-invoice">
          <div className="text-[11px] sm:text-xs uppercase tracking-wide text-[#6B7280] font-semibold mb-2">
            Ringkasan Invoice (Cakupan Filter)
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
            <div className="rounded-md bg-[#EFF6FF] border border-[#DBEAFE] p-2 sm:p-3"
                 data-testid="ringkasan-total-pembayaran">
              <div className="text-[10px] text-[#1D4ED8] uppercase font-semibold">Total Pembayaran</div>
              <div className="font-mono font-black text-sm sm:text-base text-[#1E3A8A] break-words mt-0.5">
                {formatRupiah(summary.total)}
              </div>
              <div className="text-[9px] text-[#3B82F6] mt-0.5">Nominal total invoice</div>
            </div>
            <div className="rounded-md bg-[#ECFDF5] border border-[#D1FAE5] p-2 sm:p-3"
                 data-testid="ringkasan-lunas">
              <div className="text-[10px] text-[#047857] uppercase font-semibold">Lunas</div>
              <div className="font-mono font-black text-sm sm:text-base text-[#065F46] break-words mt-0.5">
                {formatRupiah(summary.nominalLunas)}
              </div>
              <div className="text-[9px] text-[#059669] mt-0.5">
                {summary.countLunas.toLocaleString("id-ID")} invoice
              </div>
            </div>
            <div className="rounded-md bg-[#FFFBEB] border border-[#FEF3C7] p-2 sm:p-3"
                 data-testid="ringkasan-sebagian">
              <div className="text-[10px] text-[#B45309] uppercase font-semibold">Sebagian</div>
              <div className="font-mono font-black text-sm sm:text-base text-[#92400E] break-words mt-0.5">
                {formatRupiah(summary.nominalSebagian)}
              </div>
              <div className="text-[9px] text-[#D97706] mt-0.5">
                {summary.countSebagian.toLocaleString("id-ID")} invoice
              </div>
            </div>
            <div className="rounded-md bg-[#FEF2F2] border border-[#FEE2E2] p-2 sm:p-3"
                 data-testid="ringkasan-belum-lunas">
              <div className="text-[10px] text-[#B91C1C] uppercase font-semibold">Belum Lunas</div>
              <div className="font-mono font-black text-sm sm:text-base text-[#991B1B] break-words mt-0.5">
                {formatRupiah(summary.nominalBelum)}
              </div>
              <div className="text-[9px] text-[#DC2626] mt-0.5">
                {summary.countBelum.toLocaleString("id-ID")} invoice
              </div>
            </div>
            {(() => {
              const kas = parseInt(paySummary?.total_penerimaan_kas || 0, 10);
              const selisih = kas - summary.total;
              return (
                <div className="rounded-md bg-[#F5F3FF] border border-[#EDE9FE] p-2 sm:p-3 col-span-2 lg:col-span-1"
                     data-testid="ringkasan-konsolidasi">
                  <div className="text-[10px] text-[#6D28D9] uppercase font-semibold">
                    Selisih Rekonsiliasi
                  </div>
                  <div className="font-mono font-black text-sm sm:text-base text-[#5B21B6] break-words mt-0.5">
                    {selisih >= 0 ? "+" : "−"}{formatRupiah(Math.abs(selisih))}
                  </div>
                  <div className="text-[9px] text-[#7C3AED] mt-0.5 leading-tight">
                    Penerimaan kas − total invoice (bagian dari kas, bukan penerimaan baru)
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] mt-2 leading-snug">
            Invoice dibatalkan & invoice sumber carry-forward tidak dihitung. Nominal per status = Σ total invoice (nominal invoice, bukan nominal yang sudah dibayar). Selisih rekonsiliasi berasal dari data aktual dan tidak ditambahkan kembali ke total.
          </div>
        </div>
      )}

      {/* Pagination panel ATAS — konsisten dgn Invoice & Customer Billing */}
      {!loading && totalItems > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 text-xs bg-white border rounded-md px-3 py-2"
          data-testid="kolektor-pagination-top"
        >
          <div className="flex items-center gap-2">
            <span className="text-[#6B7280]">Per halaman</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10) || 10)}
              className="border rounded-md px-2 py-1 bg-white"
              data-testid="kolektor-page-size"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="text-[#6B7280]">
              {`Menampilkan ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalItems)} dari ${totalItems}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="kolektor-page-prev"
            >
              Sebelumnya
            </Button>
            <span className="font-mono" data-testid="kolektor-page-indicator">
              {page} / {totalPages}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="kolektor-page-next"
            >
              Berikutnya
            </Button>
          </div>
        </div>
      )}

      <ul className="grid grid-cols-1 gap-3" data-testid="kolektor-invoice-list">
        {pagedInvoices.map((inv) => {
          const cust = customerMap[inv.customer_id] || {};
          const alamat = cust.alamat;
          const noHp = cust.no_hp;
          const lat = Number(cust.latitude);
          const lon = Number(cust.longitude);
          const hasCoords =
            cust.latitude !== undefined && cust.latitude !== null && cust.latitude !== "" &&
            cust.longitude !== undefined && cust.longitude !== null && cust.longitude !== "" &&
            Number.isFinite(lat) && Number.isFinite(lon) &&
            lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
          const mapsUrl = hasCoords
            ? `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`
            : null;
          return (
          <li key={inv.invoice_id} className="border rounded-lg p-4 bg-white shadow-sm"
              data-testid={`kolektor-invoice-${inv.invoice_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold truncate">{inv.customer_name_snapshot}</div>
                <div className="text-xs text-[#6B7280] font-mono">{inv.customer_id}</div>
                <div className="text-xs text-[#6B7280]">
                  {inv.invoice_id} · {periodeLabel(inv.periode_tagihan)} · {inv.package_name_snapshot}
                </div>
                <div className="mt-1.5 text-xs" data-testid={`kolektor-alamat-${inv.invoice_id}`}>
                  <span className="text-[#6B7280]">Alamat: </span>
                  {alamat
                    ? <span className="text-[#111827] whitespace-pre-line">{alamat}</span>
                    : <span className="text-[#9CA3AF] italic">Alamat belum tersedia</span>}
                </div>
                <div className="text-xs mt-0.5">
                  {hasCoords ? (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#0055FF] hover:underline font-semibold"
                      data-testid={`kolektor-map-btn-${inv.invoice_id}`}
                    >
                      <MapPin className="w-3.5 h-3.5" /> Buka Google Maps
                    </a>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[#9CA3AF] italic cursor-not-allowed"
                      data-testid={`kolektor-map-empty-${inv.invoice_id}`}
                      aria-disabled="true"
                    >
                      <MapPin className="w-3.5 h-3.5" /> Lokasi belum tersedia
                    </span>
                  )}
                  {myLoc && hasCoords && Number.isFinite(distanceByCustomer[inv.customer_id]) && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-[#6B7280]"
                      data-testid={`kolektor-distance-${inv.invoice_id}`}
                    >
                      · {formatDistance(distanceByCustomer[inv.customer_id])} dari lokasi Anda
                    </span>
                  )}
                </div>
                <div className="text-xs" data-testid={`kolektor-hp-${inv.invoice_id}`}>
                  <span className="text-[#6B7280]">No. HP: </span>
                  {noHp
                    ? <a href={`tel:${noHp.replace(/\s+/g, "")}`}
                         className="text-[#1D4ED8] hover:underline font-mono"
                         data-testid={`kolektor-hp-link-${inv.invoice_id}`}>
                        {noHp}
                      </a>
                    : <span className="text-[#9CA3AF] italic">No. HP belum tersedia</span>}
                </div>
              </div>
              <Badge className={`${STATUS_COLOR[inv.status] || ""} text-xs`}
                     data-testid={`kolektor-status-${inv.invoice_id}`}>
                {STATUS_LABEL[inv.status] || inv.status}
              </Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-[#6B7280]">Total</div><div className="font-mono font-semibold">{formatRupiah(inv.total_harus_dibayar)}</div></div>
              <div><div className="text-[#6B7280]">Dibayar</div><div className="font-mono">{formatRupiah(inv.total_dibayar)}</div></div>
              <div><div className="text-[#6B7280]">Sisa</div><div className="font-mono text-[#991B1B] font-semibold">{formatRupiah(inv.sisa_tagihan)}</div></div>
            </div>
            {inv.bon_previous_amount > 0 && (
              <div className="mt-2 text-xs bg-[#FFFBEB] border border-[#FDE68A] rounded p-2"
                   data-testid={`kolektor-bon-${inv.invoice_id}`}>
                <div className="font-semibold text-[#92400E]">Termasuk bon periode sebelumnya: {formatRupiah(inv.bon_previous_amount)}</div>
                {inv.bon_source_invoice_ids?.length ? (
                  <div className="text-[#92400E]">Bon dari: {inv.bon_source_invoice_ids.join(", ")}</div>
                ) : null}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {inv.status !== "LUNAS" && inv.status !== "DIBATALKAN" && !inv.carry_forward_to && (
                <Button size="sm" onClick={() => openPay(inv)}
                        data-testid={`kolektor-pay-btn-${inv.invoice_id}`}>
                  <Wallet className="w-4 h-4 mr-1" /> Bayar
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => openPay(inv)}
                      data-testid={`kolektor-detail-btn-${inv.invoice_id}`}>
                <Receipt className="w-4 h-4 mr-1" /> Detail
              </Button>
            </div>
          </li>
          );
        })}
      </ul>

      {/* Dialog Pembayaran */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="w-[95vw] max-w-lg sm:max-w-lg p-4 sm:p-6 overflow-y-auto max-h-[92vh]" data-testid="kolektor-pay-dialog">
          <DialogHeader>
            <DialogTitle>Bayar Tagihan</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="border rounded p-3 bg-[#F8F9FA]">
                <div className="font-semibold">{selected.customer_name_snapshot}</div>
                <div className="text-xs text-[#6B7280] font-mono">{selected.invoice_id}</div>
                <div className="text-xs">{periodeLabel(selected.periode_tagihan)} · {selected.package_name_snapshot}</div>
                {selected._customer?.alamat && (
                  <div className="text-xs mt-1">{selected._customer.alamat}</div>
                )}
                {selected._customer?.latitude && selected._customer?.longitude && (
                  <a
                    className="inline-flex items-center gap-1 text-xs text-[#0055FF] mt-1"
                    href={`https://www.google.com/maps?q=${selected._customer.latitude},${selected._customer.longitude}`}
                    target="_blank" rel="noreferrer"
                    data-testid="kolektor-map-link"
                  >
                    <MapPin className="w-3 h-3" /> Lihat di Google Maps
                  </a>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-[#6B7280]">Total Tagihan</div><div className="font-mono font-semibold">{formatRupiah(selected.total_harus_dibayar)}</div></div>
                <div><div className="text-[#6B7280]">Sudah Dibayar</div><div className="font-mono">{formatRupiah(selected.total_dibayar)}</div></div>
                <div><div className="text-[#6B7280]">Sisa</div><div className="font-mono text-[#991B1B] font-semibold">{formatRupiah(selected.sisa_tagihan)}</div></div>
              </div>

              {selected.bon_previous_amount > 0 && (
                <div className="text-xs bg-[#FFFBEB] border border-[#FDE68A] rounded p-2">
                  Tagihan bulan berjalan: {formatRupiah(selected.total_tagihan)}<br />
                  Bon bulan sebelumnya: {formatRupiah(selected.bon_previous_amount)}<br />
                  <b>Total harus dibayar: {formatRupiah(selected.total_harus_dibayar)}</b>
                </div>
              )}

              {selected.status !== "LUNAS" && selected.status !== "DIBATALKAN" && !selected.carry_forward_to && (
                <form onSubmit={submitPay} className="space-y-3 border-t pt-3">
                  <div>
                    <Label>Jenis Pembayaran</Label>
                    <div className="flex gap-2 mt-1">
                      {[
                        { key: "FULL", label: "Bayar Penuh" },
                        { key: "PARTIAL", label: "Bayar Sebagian" },
                      ].map((m) => (
                        <button
                          type="button"
                          key={m.key}
                          onClick={() => setPayMode(m.key)}
                          data-testid={`kolektor-paymode-${m.key}`}
                          className={`flex-1 border rounded px-3 py-2 text-sm font-semibold ${payMode === m.key ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white text-[#111827]"}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Jumlah Diterima (Rp)</Label>
                    {payMode === "FULL" ? (
                      <div className="mt-1 border rounded px-3 py-2 bg-[#F3F4F6] font-mono font-semibold"
                           data-testid="kolektor-pay-amount-locked">
                        {formatRupiah(selected.sisa_tagihan)}
                      </div>
                    ) : (
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="1"
                        max={selected.sisa_tagihan}
                        value={payForm.amount}
                        onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                        required
                        autoFocus
                        data-testid="kolektor-pay-amount"
                      />
                    )}
                    <div className="text-xs text-[#6B7280] mt-1">
                      {payMode === "FULL"
                        ? "Otomatis mengikuti sisa tagihan saat ini."
                        : `Sisa tagihan: ${formatRupiah(selected.sisa_tagihan)}. Backend akan menolak bila melebihi.`}
                    </div>
                  </div>
                  <div>
                    <Label>Metode Pembayaran</Label>
                    <div className="flex gap-2 mt-1">
                      {["TUNAI", "TRANSFER"].map((m) => (
                        <label key={m} data-testid={`kolektor-pay-method-opt-${m}`} className={`flex-1 border rounded px-3 py-2 cursor-pointer text-center ${payForm.method === m ? "bg-[#0055FF] text-white" : ""}`}>
                          <input
                            type="radio"
                            name="method"
                            value={m}
                            checked={payForm.method === m}
                            onChange={() => { setPayForm({ ...payForm, method: m }); if (m !== "TRANSFER") { setProofFile(null); setProofPreview(""); } }}
                            className="hidden"
                            data-testid={`kolektor-pay-method-${m}`}
                          />
                          {m}
                        </label>
                      ))}
                    </div>
                    <div className="text-[10px] text-[#6B7280] mt-1">
                      TUNAI = uang diterima Anda. TRANSFER = customer transfer ke rekening perusahaan/admin (wajib bukti).
                    </div>
                  </div>

                  {payForm.method === "TRANSFER" && (
                    <div data-testid="kolektor-proof-block">
                      <Label>Bukti Transfer <span className="text-[#991B1B]">*</span></Label>
                      <div className="mt-1">
                        {!proofFile ? (
                          <div className="grid grid-cols-2 gap-2" data-testid="kolektor-proof-drop">
                            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#DBE4FF] rounded-md py-4 cursor-pointer text-[#0055FF] hover:bg-[#F0F4FF]">
                              <Camera className="w-4 h-4" />
                              <span className="text-xs font-semibold">Ambil Foto</span>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={onProofFileChange}
                                data-testid="kolektor-proof-camera-input"
                              />
                            </label>
                            <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#DBE4FF] rounded-md py-4 cursor-pointer text-[#0055FF] hover:bg-[#F0F4FF]">
                              <Upload className="w-4 h-4" />
                              <span className="text-xs font-semibold">Pilih Foto/PDF</span>
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                className="hidden"
                                onChange={onProofFileChange}
                                data-testid="kolektor-proof-input"
                              />
                            </label>
                          </div>
                        ) : (
                          <div className="flex items-start gap-3 border rounded-md p-2 bg-[#F8F9FA]" data-testid="kolektor-proof-preview">
                            {proofPreview ? (
                              <img src={proofPreview} alt="Preview bukti" className="w-20 h-20 object-cover rounded border" />
                            ) : (
                              <div className="w-20 h-20 flex items-center justify-center rounded border bg-white text-[#6B7280]">
                                <FileText className="w-8 h-8" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1 text-xs">
                              <div className="font-semibold truncate">{proofFile.name}</div>
                              <div className="text-[#6B7280]">{(proofFile.size / 1024).toFixed(1)} KB · {proofFile.type || "-"}</div>
                              <button type="button"
                                      onClick={() => { setProofFile(null); setProofPreview(""); }}
                                      className="mt-1 inline-flex items-center gap-1 text-[#991B1B] hover:underline"
                                      data-testid="kolektor-proof-remove">
                                <X className="w-3 h-3" /> Hapus & pilih ulang
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="text-[10px] text-[#6B7280] mt-1">
                          Foto struk / screenshot m-banking. Maks 8 MB. Format: JPG/PNG/WebP/HEIC/PDF.
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Catatan (opsional)</Label>
                    <Textarea
                      value={payForm.notes}
                      onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                      rows={2}
                      data-testid="kolektor-pay-notes"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setSelected(null)} disabled={paying}>
                      Batal
                    </Button>
                    <Button type="submit" disabled={paying} data-testid="kolektor-pay-submit">
                      {paying
                        ? "Menyimpan…"
                        : (() => {
                            const nominal = payMode === "FULL"
                              ? parseInt(selected.sisa_tagihan || 0, 10)
                              : parseInt(payForm.amount || 0, 10);
                            return nominal > 0 ? `Bayar ${formatRupiah(nominal)}` : "Bayar";
                          })()}
                    </Button>
                  </DialogFooter>
                </form>
              )}

              {/* Riwayat Pembayaran */}
              <PaymentHistory invoiceId={selected.invoice_id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PaymentHistory({ invoiceId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await api.get("/billing/payments", { params: { invoice_id: invoiceId } });
        if (ok) setRows(data.items || []);
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [invoiceId]);

  const viewProof = async (p) => {
    try {
      const resp = await api.get(`/billing/payments/${p.payment_id}/proof`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal memuat bukti");
    }
  };

  return (
    <div className="border-t pt-3 mt-3" data-testid="payment-history">
      <div className="font-semibold text-xs mb-2">Riwayat Pembayaran</div>
      {loading && <div className="text-xs text-[#6B7280]">Memuat…</div>}
      {!loading && rows.length === 0 && <div className="text-xs text-[#6B7280]">Belum ada pembayaran.</div>}
      <ul className="space-y-2">
        {rows.map((p) => (
          <li key={p.payment_id} className={`text-xs p-2 rounded border ${p.status === "VOID" ? "bg-[#F3F4F6] text-[#6B7280] line-through" : "bg-white"}`}
              data-testid={`payment-row-${p.payment_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono">{p.payment_id}</span>
              <span className={`px-1.5 py-0.5 rounded ${p.status === "VOID" ? "bg-[#E5E7EB]" : "bg-[#D1FAE5] text-[#065F46]"}`}>
                {p.status}
              </span>
            </div>
            <div>{p.payment_date} · {methodLabel(p.method)} · <b className="font-mono">{formatRupiah(p.amount_received)}</b></div>
            <div className="text-[10px]" data-testid={`payment-received-${p.payment_id}`}>
              Diterima oleh: <b>{receivedLabel(p).label}</b>
            </div>
            <div className="text-[10px]">Dicatat oleh: {p.recorded_by}</div>
            {p.notes && <div className="italic">{p.notes}</div>}
            {p.proof_url && (
              <button type="button" onClick={() => viewProof(p)}
                      className="mt-1 inline-flex items-center gap-1 text-[#0055FF] hover:underline"
                      data-testid={`payment-view-proof-${p.payment_id}`}>
                <ImageIcon className="w-3 h-3" /> Lihat bukti transfer
              </button>
            )}
            {p.status === "VOID" && <div className="text-[#991B1B]">Alasan void: {p.void_reason}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
